import { CreateBookingDataParams } from "../../adapters/create-booking/create-booking.types";
import type { PoType } from "@/features/license-fee/list/utils/private-office-contract-mappings";
import {
  PackageDetails,
  PackageVersion,
  Company,
} from "../../layouts/booking-create-page/booking-create-page.types";

export type BookingData = {
  id?: number;
  company: {
    id: number;
    name: string;
  };
  package: {
    id: number;
    name: string;
  };
  coworking: {
    id: number;
    name: string;
  };
  private_room: {
    id: number;
    searchId?: number;
    name: string;
  };
  activeCycle: PackageVersion;
  start_date: string;
  months: string;
  credits: string;
  take: string;
  members: string;
  flow_type: FlowType;
};

export type UpsellData = {
  upsellOption: number;
  upsellProRataOption: number;
  simulatedUpsellData: SimulatedUpsell;
};

export type PoContractCreateData = {
  companyId: string;
  packageId: string;
  startDate: string;
  endDate?: string;
  contractResponsibleUser?: string;
  roomResponsibleUser?: string;
  members: number;
  coworkingId: string;
  privateOfficeId?: string;
  dealId: string;
  contractType: string;
  hasUndeterminedTime: boolean;
  monthlyCredits: number;
  partnerValue: string;
  takeRatePercentage: number;
  repass: number;
  noticeInDaysClient: number;
  noticeInDaysPartner: number;
  expectedDurationMonths: number;
  notes?: string;
  renewalIndicator: string;
  acceptanceTerm: string;
  acceptanceTermFiles: { id: string; url: string; name: string }[];
  knowledgeTerm: string;
  knowledgeTermFiles: { id: string; url: string; name: string }[];
  flowType: FlowType;
  poType?: PoType;
};

export type SimulateUpsellProps = {
  creditAmount: number;
  beginDate: string;
  companyId: number;
  packageId: number;
};

export type SimulatedUpsellResponse = {
  data: {
    newPayments: simulatedPayment[] | [];
    updatedCycles: simulatedUpdatedCycles[];
  };
};

export type SimulatedUpsell = {
  newPayments: simulatedPayment[] | [];
  updatedCycles: simulatedUpdatedCycles[];
};

export interface simulatedPayment {
  cycleId: number;
  amount: number;
  capacity: number;
  paymentDate: string;
  competencyMonth: string;
  invoicingCompetencyDate?: string;
  proRata: boolean | undefined;
}

export interface simulatedUpdatedCycles {
  cycleId: number;
  oldCapacity: number;
  oldAmount: number;
  newCapacity: number;
  newAmount: number;
  startDate: string;
  endDate: string;
  proRata: boolean | undefined;
}

export type BookingFlowContextData = {
  currentStep: FlowStep;
  setCurrentStep: (step: FlowStep) => void;
  flowType: FlowType;
  bookingData: BookingData | undefined;
  upsellData: UpsellData | null;
  poContractCreateData: PoContractCreateData | undefined;
  bookingFlowStatus: BookingFlowStatus;
  errorMessages: ErrorMessages;
  setFlowType: (type: FlowType) => void;
  nextStep: () => void;
  previousStep: () => void;
  resetFlow: () => void;
  setBookingData: (data: any) => void;
  setUpsellData: (data: any) => void;
  setPoContractCreateData: (
    data: PoContractCreateData & { flowType: FlowType },
  ) => void;
  SimulateUpsell: (data: any) => void;
  setSimulatedUpsell: (data: SimulatedUpsell | null) => void;
  simulatedUpsell: SimulatedUpsell | null;
  setSelectedCycle: (cycle: PackageVersion) => void;
  selectedCycle: PackageVersion | null;
  setSelectedPackage: (packageDetails: PackageDetails) => void;
  selectedPackage: PackageDetails | null;
  setSelectedCompany: (company: Company) => void;
  selectedCompany: Company | null;
  setSelectedPackageGroups: (groups: { name: string }[]) => void;
  selectedPackageGroups: { name: string }[] | null;
  UpdateUpsellPayments: (payments: simulatedPayment[]) => void;
  UpdateUpsellCycles: (cycles: simulatedUpdatedCycles[]) => void;
  setBookingFlowStatus: (status: Partial<BookingFlowStatus>) => void;
  setErrorMessages: (messages: Partial<ErrorMessages>) => void;
  handleSubmitBookingData: (data: CreateBookingDataParams) => void;
  handleSubmitPoContractData: (
    data: PoContractCreateData & { flowType: FlowType },
  ) => void;
  processingStatus: ProcessingStatus;
  setProcessingStatus: (status: Partial<ProcessingStatus>) => void;
  moveToSummaryStep: () => void;
};

export type BookingFlowProviderProps = {
  children: React.ReactNode;
};

export type ProcessingStatus = {
  isProcessing: boolean;
  canNavigateAway: boolean;
  currentOperation: "booking" | "upsell" | "po-contract" | null;
};

export type BookingFlowStatus = {
  booking: "success" | "error" | null;
  upsell: "success" | "error" | null;
  poContract: "success" | "error" | null;
};

export type ErrorMessages = {
  booking?: string;
  upsell?: string;
  poContract?: string;
};

export enum DefaultFlowStep {
  CREATE = "DEFAULT_CREATE",
  SUMMARY = "DEFAULT_SUMMARY",
  LOADING = "DEFAULT_LOADING",
  RESULT = "DEFAULT_RESULT",
}

export enum UpsellFlowStep {
  CREATE = "UPSELL_CREATE",
  UPSELL = "UPSELL_CONFIG",
  SUMMARY = "UPSELL_SUMMARY",
  LOADING = "UPSELL_LOADING",
  RESULT = "UPSELL_RESULT",
}

export enum DefaultPoContractFlowStep {
  CREATE = "PO_CONTRACT_CREATE",
  SUMMARY = "PO_CONTRACT_SUMMARY",
  LOADING = "PO_CONTRACT_LOADING",
  RESULT = "PO_CONTRACT_RESULT",
}

export enum UpsellPoContractFlowStep {
  CREATE = "UPSELL_PO_CONTRACT_CREATE",
  UPSELL = "UPSELL_PO_CONTRACT_UPSELL",
  SUMMARY = "UPSELL_PO_CONTRACT_SUMMARY",
  LOADING = "UPSELL_PO_CONTRACT_LOADING",
  RESULT = "UPSELL_PO_CONTRACT_RESULT",
}

export type FlowStep =
  | DefaultFlowStep
  | UpsellFlowStep
  | UpsellPoContractFlowStep
  | DefaultPoContractFlowStep;
export type FlowType = "default" | "upsell";

export const FlowTransitions: Record<
  FlowStep,
  {
    next: FlowStep;
    previous?: FlowStep;
  }
> = {
  [DefaultPoContractFlowStep.CREATE]: {
    next: DefaultPoContractFlowStep.SUMMARY,
  },
  [DefaultPoContractFlowStep.SUMMARY]: {
    next: DefaultPoContractFlowStep.LOADING,
    previous: DefaultPoContractFlowStep.CREATE,
  },
  [DefaultPoContractFlowStep.LOADING]: {
    next: DefaultPoContractFlowStep.RESULT,
    previous: DefaultPoContractFlowStep.SUMMARY,
  },
  [DefaultPoContractFlowStep.RESULT]: {
    next: DefaultPoContractFlowStep.RESULT,
    previous: DefaultPoContractFlowStep.LOADING,
  },
  [UpsellPoContractFlowStep.CREATE]: {
    next: UpsellPoContractFlowStep.UPSELL,
  },
  [UpsellPoContractFlowStep.UPSELL]: {
    next: UpsellPoContractFlowStep.SUMMARY,
    previous: UpsellPoContractFlowStep.CREATE,
  },
  [UpsellPoContractFlowStep.SUMMARY]: {
    next: UpsellPoContractFlowStep.LOADING,
    previous: UpsellPoContractFlowStep.UPSELL,
  },
  [UpsellPoContractFlowStep.LOADING]: {
    next: UpsellPoContractFlowStep.RESULT,
    previous: UpsellPoContractFlowStep.SUMMARY,
  },
  [UpsellPoContractFlowStep.RESULT]: {
    next: UpsellPoContractFlowStep.RESULT,
    previous: UpsellPoContractFlowStep.LOADING,
  },
  [DefaultFlowStep.CREATE]: {
    next: DefaultFlowStep.SUMMARY,
  },
  [DefaultFlowStep.SUMMARY]: {
    next: DefaultFlowStep.LOADING,
    previous: DefaultFlowStep.CREATE,
  },
  [DefaultFlowStep.LOADING]: {
    next: DefaultFlowStep.RESULT,
    previous: DefaultFlowStep.SUMMARY,
  },
  [DefaultFlowStep.RESULT]: {
    next: DefaultFlowStep.RESULT,
    previous: DefaultFlowStep.LOADING,
  },
  [UpsellFlowStep.CREATE]: {
    next: UpsellFlowStep.UPSELL,
  },
  [UpsellFlowStep.UPSELL]: {
    next: UpsellFlowStep.SUMMARY,
    previous: UpsellFlowStep.CREATE,
  },
  [UpsellFlowStep.SUMMARY]: {
    next: UpsellFlowStep.LOADING,
    previous: UpsellFlowStep.UPSELL,
  },
  [UpsellFlowStep.LOADING]: {
    next: UpsellFlowStep.RESULT,
    previous: UpsellFlowStep.SUMMARY,
  },
  [UpsellFlowStep.RESULT]: {
    next: UpsellFlowStep.RESULT,
    previous: UpsellFlowStep.LOADING,
  },
} as const;
